import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
} from "@mui/material";
import apiService from "../../services/api";
import CustomLoading from "../../components/common/CustomLoading";
import {
  formatMobileNumber,
  convertTimestampToIST,
} from "../../utils/formatters";
import { parsePaymentSummaryForPlanVehicle } from "../../utils/paymentSummaryParse";
import { useNotification } from "../../contexts/NotificationContext";

function isTruthyRefundAddedFlag(v: unknown): boolean {
  return (
    v === true ||
    v === 1 ||
    (typeof v === "string" && v.trim().toLowerCase() === "true")
  );
}
function isPaymentRefundAlreadyAdded(p: Record<string, unknown>): boolean {
  return (
    isTruthyRefundAddedFlag(p.isrefundadded) ||
    isTruthyRefundAddedFlag(p.isRefundAdded) ||
    isTruthyRefundAddedFlag(p.IsRefundAdded)
  );
}
const PaymentDetails: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { id } = useParams<{ id: string }>();
  const [paymentData, setPaymentData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundRemark, setRefundRemark] = useState("");
  const [refundDescription, setRefundDescription] = useState("");
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [refundSuccess, setRefundSuccess] = useState<string | null>(null);
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  useEffect(() => {
    if (id) {
      fetchPaymentDetails(id);
    }
  }, [id]);

  const fetchPaymentDetails = async (paymentId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getPaymentDetails(paymentId);
      const paymentDetails = (response as any)?.data || response;
      setPaymentData(paymentDetails);
    } catch (error: any) {
      setError("Failed to load payment details. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const issueCouponHeaderBlue = "rgba(22, 67, 135, 1)";
  const issueCouponSubmitYellow = "#FFEB3B";
  const issueCouponPrimaryButtonSx = {
    backgroundColor: issueCouponSubmitYellow,
    color: issueCouponHeaderBlue,
    fontWeight: 700,
    textTransform: "none" as const,
    px: 2.5,
    py: 1,
    minHeight: 40,
    borderRadius: 1,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#FDD835",
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "rgba(255, 235, 59, 0.5)",
      color: "rgba(22, 67, 135, 0.6)",
    },
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      const date = convertTimestampToIST(dateString);
      if (!date) return "N/A";
      const month = date.toLocaleString("en-US", { month: "short" });
      const day = date.getDate();
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = (hours % 12 || 12).toString().padStart(2, "0");
      const displayMinutes = minutes.toString().padStart(2, "0");
      return `${month} ${day} ${year}, ${displayHours}:${displayMinutes} ${ampm}`;
    } catch {
      return dateString;
    }
  };

  const formatDiscountedAmount = (raw: unknown): string => {
    if (raw == null || raw === "") return "";
    const numericRaw =
      typeof raw === "number" || typeof raw === "string" ? raw : "";
    const n =
      typeof numericRaw === "number"
        ? numericRaw
        : Number.parseFloat(numericRaw);
    if (!Number.isFinite(n)) return "";
    return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPlanVehicle = (summaryText: string) => {
    if (!summaryText || summaryText === "N/A") {
      return { plan: "N/A", vehicle: "N/A" };
    }
    const { plan, vehicle } = parsePaymentSummaryForPlanVehicle(summaryText);
    return {
      plan: plan || "N/A",
      vehicle: vehicle || "N/A",
    };
  };

  const toNumber = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const openRefundDialog = () => {
    const fallbackDescription =
      typeof paymentData?.summeryofpayments === "string"
        ? paymentData.summeryofpayments
        : "";
    setRefundDescription(fallbackDescription);
    setRefundRemark("");
    setRefundError(null);
    setRefundSuccess(null);
    setRefundDialogOpen(true);
  };

  const closeRefundDialog = () => {
    if (refundSubmitting) return;
    setRefundDialogOpen(false);
    setRefundError(null);
  };

  const handleProcessRefund = async () => {
    const bookingmasterid = toNumber(
      paymentData?.bookingmasterid ?? paymentData?.bookingid,
    );
    const bookingplanid = toNumber(
      paymentData?.bookingplanid ??
        paymentData?.planid ??
        paymentData?.bookingPlanId,
    );
    const amount = toNumber(paymentData?.amount);
    const remark = refundRemark.trim();

    if (bookingmasterid <= 0) {
      setRefundError("Booking ID is missing in payment details.");
      return;
    }
    if (amount <= 0) {
      setRefundError("Refund amount must be greater than 0.");
      return;
    }
    if (remark === "") {
      setRefundError("Please enter a remark.");
      return;
    }

    setRefundSubmitting(true);
    setRefundError(null);
    setRefundSuccess(null);
    try {
      const res = (await apiService.addRefund([
        {
          bookingmasterid,
          bookingplanid,
          amount,
          refunddescription: remark,
          refundType: "0",
          isaddedbyweb: true,
        },
      ])) as { isSuccess?: boolean; message?: string };

      if (res && typeof res === "object" && res.isSuccess === false) {
        throw new Error(
          typeof res.message === "string" && res.message.trim() !== ""
            ? res.message
            : "Refund request failed.",
        );
      }

      const msg =
        typeof res?.message === "string" && res.message.trim() !== ""
          ? res.message.trim()
          : "Refund request submitted successfully.";

      setRefundDialogOpen(false);
      showNotification(msg, "success");
      navigate("/payment-management");
    } catch (e: unknown) {
      const msg =
        e instanceof Error && e.message.trim() !== ""
          ? e.message
          : "Failed to process refund.";

      showNotification(msg, "error");
    } finally {
      setRefundSubmitting(false);
    }
  };

  if (loading) {
    return <CustomLoading message="Fetching Payment Details..." />;
  }

  if (error) {
    return (
      <Container fluid>
        <Alert severity="error" className="mb-3">
          {error}
        </Alert>
        <Button variant="outlined" onClick={onBack}>
          Go Back
        </Button>
      </Container>
    );
  }

  if (!paymentData) {
    return (
      <Container fluid>
        <Alert severity="warning" className="mb-3">
          No payment data found.
        </Alert>
        <Button variant="outlined" onClick={onBack}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="pb-5">
      {/* Basic Information Section */}
      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          {/* Header with Status */}
          <Box className="d-flex justify-content-between align-items-center mb-3">
            <Typography
              variant="h6"
              className="fw-bold mb-0"
              sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
            >
              Payment Information
            </Typography>
          </Box>

          {/* Divider Line */}
          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          {/* Basic Information Grid */}
          <Row className="g-4">
            {[
              { label: "Payment ID", value: paymentData.paymentid || "N/A" },
              { label: "Booking ID", value: paymentData.bookingid || "N/A" },
              { label: "User Name", value: paymentData.username || "N/A" },
              {
                label: "Mobile Number",
                value: formatMobileNumber(paymentData.mobileno) || "N/A",
              },
              {
                label: "Amount",
                value: paymentData.amount ? `₹${paymentData.amount}` : "N/A",
              },
              {
                label: "Discounted Amount",
                value: formatDiscountedAmount(
                  paymentData.discountedamount ?? paymentData.discountedAmount,
                ),
              },
            ].map((field, idx) => (
              <Col xs={12} sm={6} md={4} key={idx}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6c757d",
                      mb: 0.5,
                      fontSize: "0.85rem",
                      fontWeight: 500,
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="fw-bold"
                    sx={{
                      color: "#495057",
                      fontSize: "0.95rem",
                      wordBreak: "break-word",
                    }}
                  >
                    {field.value}
                  </Typography>
                </Box>
              </Col>
            ))}
          </Row>
        </Box>
      </Paper>

      {/* Payment Summary Section */}
      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          {/* Header */}
          <Typography
            variant="h6"
            className="fw-bold mb-3"
            sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
          >
            Payment Summary
          </Typography>

          {/* Divider Line */}
          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          {/* Payment Summary Information Grid */}
          <Row className="g-4">
            {(() => {
              const { plan, vehicle } = formatPlanVehicle(
                paymentData.summeryofpayments,
              );
              return [
                { label: "Plan", value: plan },
                { label: "Vehicle", value: vehicle },
                { label: "Hub Name", value: paymentData.hubname || "N/A" },
                {
                  label: "Transaction ID",
                  value: paymentData.transactionid || "N/A",
                },
                {
                  label: "Payment Date",
                  value: formatDateTime(paymentData.enteredon),
                },
              ].map((field, idx) => (
                <Col xs={12} sm={6} md={idx === 4 ? 12 : 3} key={idx}>
                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6c757d",
                        mb: 0.5,
                        fontSize: "0.85rem",
                        fontWeight: 500,
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      className="fw-bold"
                      sx={{
                        color: "#495057",
                        fontSize: "0.95rem",
                        wordBreak: "break-word",
                      }}
                    >
                      {field.value}
                    </Typography>
                  </Box>
                </Col>
              ));
            })()}
          </Row>
        </Box>
      </Paper>

      {/* Action Buttons — refund only for booking-related payment summaries, and not if refund already recorded */}
      {String(
        paymentData.summeryofpayments ?? paymentData.summeryOfPayments ?? "",
      )
        .toLowerCase()
        .includes("booking") && !isPaymentRefundAlreadyAdded(paymentData) ? (
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: { xs: "stretch", md: "flex-end" },
          }}
        >
          <Button
            variant="contained"
            onClick={openRefundDialog}
            sx={{
              backgroundColor: "rgba(22, 67, 135, 1)",
              textTransform: "none",
              fontWeight: 600,
              minWidth: { xs: "100%", md: 180 },
              "&:hover": { backgroundColor: "rgba(18, 57, 116, 1)" },
            }}
            style={{
              marginRight: "10px",
            }}
          >
            Process Refund
          </Button>
          <Button
            onClick={() =>
              navigate("/payment-management", {
                state: {
                  filters: { paymentstatus: 0 },
                  page: 0,
                  shouldRefetch: true,
                },
              })
            }
            sx={{
              color: issueCouponHeaderBlue,
              fontWeight: 600,
              textTransform: "none",
              border: "1px solid rgba(22, 67, 135, 0.6)",
            }}
          >
            cancel
          </Button>
        </Box>
      ) : null}

      <Dialog
        open={refundDialogOpen}
        onClose={closeRefundDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            m: 0,
            py: 1.75,
            px: 2,
            backgroundColor: issueCouponHeaderBlue,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 700,
            fontSize: "1.125rem",
          }}
        >
          Process Refund
        </DialogTitle>
        <DialogContent
          sx={{
            px: 2.5,
            pb: 2,
            pt: "15px !important",
            position: "relative",
          }}
        >
          {refundError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {refundError}
            </Alert>
          ) : null}

          <TextField
            fullWidth
            required
            label="Remark"
            value={refundRemark}
            onChange={(e) => setRefundRemark(e.target.value)}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            pt: 1,
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            onClick={closeRefundDialog}
            disabled={refundSubmitting}
            sx={{
              color: issueCouponHeaderBlue,
              fontWeight: 600,
              textTransform: "none",
              border: "1px solid rgba(22, 67, 135, 0.6)",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleProcessRefund()}
            disabled={refundSubmitting}
            sx={issueCouponPrimaryButtonSx}
          >
            {refundSubmitting ? "Submitting..." : "Submit Refund"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentDetails;
